import { Flame } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useUpdateIntensityLevel } from '@/hooks/coach/use-coach-sessions';

type Level = 'low' | 'medium' | 'high';

const LEVELS: { value: Level; label: string; activeClass: string }[] = [
  { value: 'low',    label: 'Low',    activeClass: 'bg-green-500 border-green-500 text-white' },
  { value: 'medium', label: 'Medium', activeClass: 'bg-amber-500 border-amber-500 text-white' },
  { value: 'high',   label: 'High',   activeClass: 'bg-red-500 border-red-500 text-white' },
];

interface IntensityLevelProps {
  sessionId: string;
  value: Level | null;
}

export function IntensityLevel({ sessionId, value }: IntensityLevelProps) {
  const { mutateAsync, isPending } = useUpdateIntensityLevel(sessionId);

  const handleTap = async (level: Level) => {
    // Tap active level to deselect (back to null)
    await mutateAsync(value === level ? null : level);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Flame className="w-4 h-4" />
          Session Intensity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          {LEVELS.map(({ value: level, label, activeClass }) => (
            <button
              key={level}
              onClick={() => handleTap(level)}
              disabled={isPending}
              className={cn(
                'flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-colors',
                value === level
                  ? activeClass
                  : 'border-border bg-background text-muted-foreground hover:bg-muted'
              )}
            >
              {label}
            </button>
          ))}
        </div>
        {!value && (
          <p className="text-xs text-muted-foreground mt-2">Optional — tap to record session intensity</p>
        )}
      </CardContent>
    </Card>
  );
}
